import { PlaceholderMap } from '../../shared/placeholders';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { AuthenticatedImageComponent } from '../../../../components/authenticated-image/authenticated-image.component';
import { CatalogEntry, DesignElement } from '../../../../services/voucher-design.service';
import { ElementIssues } from '../../voucher-design-editor.store';
import {
  Handle,
  PX_PER_PT,
  PageSize,
  Rect,
  clampRect,
  minSizeFor,
  SnapGuides,
  rectsIntersect,
  resizeRect,
  selectionBounds,
  snapMove,
  snapResize,
} from '../../editor-geometry';
import { PageRulerComponent } from './components/page-ruler/page-ruler.component';
import {
  CanvasElementComponent,
  KeyResize,
  PointerStart,
} from './components/canvas-element/canvas-element.component';

/** A drag in progress: where it started and the element's rect at that moment. */
interface Interaction {
  id: string;
  mode: 'move' | 'resize' | 'pan';
  handle?: Handle;
  /** For a pan: the picture's pixel size and the crop focus when the drag began. */
  natural?: { width: number; height: number };
  startFocus?: { x: number; y: number };
  startX: number; // client px
  startY: number;
  startRect: Rect;
  lockAspect: boolean;
  entry: CatalogEntry | undefined;
  /** For a move: every element that moves along (the selection), with its start rect. */
  companions: { id: string; rect: Rect }[];
  /** The pointer moved since it went down: a drag, not a click. */
  moved: boolean;
  /** Selected before the press (see PointerStart.wasSelected). */
  wasSelected: boolean;
}

/** A marquee being drawn on empty page: client px of its origin and the current rect in pt. */
interface Marquee {
  startX: number;
  startY: number;
  add: boolean;
  rect: Rect | null;
}

/**
 * The page: the design's PNG with the elements over it. The element layer is sized
 * in pt and scaled with a CSS transform, so every element's CSS is exactly what the
 * PDF uses; `scale` only changes how big the page appears.
 *
 * Dragging and resizing are plain pointer events: deltas in client px divided by
 * the scale are deltas in pt, clamped to the page and the kind's minimum size.
 */
@Component({
  selector: 'app-design-canvas',
  standalone: true,
  imports: [
    HlmTypographyImports,
    AuthenticatedImageComponent,
    CanvasElementComponent,
    PageRulerComponent,
  ],
  templateUrl: './design-canvas.component.html',
  styleUrls: ['./design-canvas.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesignCanvasComponent {
  /** The uploaded artwork (PNG) or the template artwork (SVG); empty for a plain page. */
  readonly previewSrc = input.required<string>();
  /** The page colour of a design without artwork. */
  readonly backgroundColor = input<string | null>(null);
  readonly page = input.required<PageSize>();
  readonly elements = input<DesignElement[]>([]);
  readonly selectedIds = input<ReadonlySet<string>>(new Set());
  readonly scale = input(1);
  readonly catalogByKind = input<Map<string, CatalogEntry>>(new Map());
  /** The catalog's placeholders by key, for the texts. */
  readonly placeholders = input<PlaceholderMap>(new Map());
  readonly fontCss = input.required<(family: string) => string>();
  /** The picture an image element shows (logo, banner, library picture), or null. */
  readonly imageSrcFor = input<(element: DesignElement) => string | null>(() => null);
  readonly longTexts = input(false);
  /** Errors and warnings per element id, shown as a badge on the element. */
  readonly issuesByElement = input<ReadonlyMap<string, ElementIssues>>(new Map());
  /** Dashed outlines on every element, or only on hover and selection. */
  readonly outlines = input(false);
  /** The text being edited in place, or null. */
  readonly editingId = input<string | null>(null);
  /** The editing started with the whole text selected (a fresh text). */
  readonly editSelectAll = input(false);

  /** A click on an element (toggle with Shift) or on the empty page (null). */
  readonly select = output<{ id: string; toggle: boolean } | null>();
  /** The marquee's catch: ids it touched, added to the selection with Shift. */
  readonly selectMany = output<{ ids: string[]; add: boolean }>();
  /** A second click on a part of the selected group. */
  readonly drillIn = output<string>();
  /** Editing a text in place: start on a double-click, the typed content, the end. */
  readonly editStart = output<string>();
  readonly editEnd = output<void>();
  readonly contentChange = output<{ id: string; content: string }>();
  /** The group the selection lies in, outlined with its label. */
  readonly groupBox = input<Rect | null>(null);
  readonly nudge = output<{ dx: number; dy: number }>();
  readonly remove = output<string>();
  /** Continuous while dragging, in pt (unrounded); a move carries the whole selection. */
  readonly elementChange = output<{ id: string; rect: Rect }[]>();
  /** Once, when the pointer is released. */
  readonly elementCommit = output<{ id: string; rect: Rect }[]>();
  /** A crop pan in progress (`commit` false) or finished (`commit` true). */
  readonly focusChange = output<{
    id: string;
    focus_x: number;
    focus_y: number;
    commit: boolean;
  }>();
  /** The scale at which the whole page fits the viewport, and the one at which its width does. */
  readonly fitScaleChange = output<{ fit: number; width: number }>();
  /** Ctrl/Cmd + wheel or a trackpad pinch over the canvas: multiply the zoom by this. */
  readonly zoomBy = output<number>();

  readonly PX_PER_PT = PX_PER_PT;
  readonly dragging = signal(false);
  readonly marquee = signal<Rect | null>(null);
  /** Smart guides while a selection moves or resizes. */
  readonly guides = signal<SnapGuides>({ x: [], y: [] });
  /** The elements whose edge or centre a guide runs through: they light up too. */
  readonly snapTargets = computed(() => {
    const { x, y } = this.guides();
    const on = (value: number, lines: number[]) => lines.some((l) => Math.abs(l - value) < 0.01);
    const hit = new Set<string>();
    if (x.length === 0 && y.length === 0) return hit;
    for (const e of this.elements()) {
      if (
        on(e.x, x) ||
        on(e.x + e.width / 2, x) ||
        on(e.x + e.width, x) ||
        on(e.y, y) ||
        on(e.y + e.height / 2, y) ||
        on(e.y + e.height, y)
      ) {
        hit.add(e.id);
      }
    }
    return hit;
  });
  /** Snap distance: a few screen pixels, whatever the zoom. */
  private readonly snapPx = 6;
  /** The dashed box around several selected elements. */
  readonly selectionBox = computed(() => {
    const ids = this.selectedIds();
    if (ids.size < 2 || this.groupBox()) return null; // a group has its own frame
    return selectionBounds(this.elements().filter((e) => ids.has(e.id)));
  });
  readonly singleSelectedId = computed(() => {
    const ids = this.selectedIds();
    return ids.size === 1 ? [...ids][0] : null;
  });
  private readonly host = viewChild.required<ElementRef<HTMLElement>>('host');
  private readonly pageEl = viewChild.required<ElementRef<HTMLElement>>('pageSheet');
  private interaction: Interaction | null = null;
  private marqueeState: Marquee | null = null;
  private readonly onMove = (event: PointerEvent) => this.pointerMove(event);
  private readonly onUp = (event: PointerEvent) => this.pointerUp(event);

  /** Where the cursor was when zooming, in page pt and client px, to keep it in place. */
  private zoomAnchor: { x: number; y: number; clientX: number; clientY: number } | null = null;

  constructor() {
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.measure());
      observer.observe(this.host().nativeElement);
      destroyRef.onDestroy(() => observer.disconnect());
      this.measure();
      // Browsers zoom the page on Ctrl/Cmd + wheel and on a trackpad pinch (which
      // arrives as a wheel event with ctrlKey). Over the canvas that zooms the editor.
      const host = this.host().nativeElement;
      const onWheel = (event: WheelEvent) => {
        if (!event.ctrlKey && !event.metaKey) return;
        event.preventDefault();
        this.zoomAnchor = {
          ...this.toPagePoint(event.clientX, event.clientY),
          clientX: event.clientX,
          clientY: event.clientY,
        };
        // a pinch sends small deltas, a wheel notch about 100: both feel right this way
        this.zoomBy.emit(Math.exp(-event.deltaY * 0.01));
      };
      host.addEventListener('wheel', onWheel, { passive: false });
      destroyRef.onDestroy(() => host.removeEventListener('wheel', onWheel));
    });
    // After a zoom, scroll so the point under the cursor stays under the cursor.
    afterRenderEffect(() => {
      this.scale();
      const anchor = this.zoomAnchor;
      if (!anchor) return;
      this.zoomAnchor = null;
      const host = this.host().nativeElement;
      const page = this.pageEl().nativeElement.getBoundingClientRect();
      const factor = PX_PER_PT * this.scale();
      host.scrollLeft += page.left + anchor.x * factor - anchor.clientX;
      host.scrollTop += page.top + anchor.y * factor - anchor.clientY;
    });
    destroyRef.onDestroy(() => this.stopListening());
    // A new print size changes the page: fit it again.
    afterRenderEffect(() => {
      this.page();
      this.measure();
    });
  }

  measure() {
    const host = this.host().nativeElement;
    const padding = 72; // p-6 plus the rulers
    // the canvas fills its column; fall back to the window when it has no height yet
    const available =
      host.clientHeight > 200 ? host.clientHeight : Math.max(480, window.innerHeight - 260);
    const page = this.page();
    const byWidth = (host.clientWidth - padding) / (page.width * PX_PER_PT);
    const byHeight = (available - padding) / (page.height * PX_PER_PT);
    const scale = Math.min(byWidth, byHeight);
    if (scale > 0 && Number.isFinite(scale))
      this.fitScaleChange.emit({ fit: scale, width: byWidth });
  }

  entryFor(element: DesignElement): CatalogEntry | undefined {
    return this.catalogByKind().get(element.kind);
  }

  private isText(element: DesignElement): boolean {
    const render = this.entryFor(element)?.render;
    return render === 'text' || render === 'multiline';
  }

  onBackgroundClick() {
    if (!this.dragging()) this.select.emit(null);
  }

  /** A press on empty page starts a marquee; a plain click (no drag) deselects on click. */
  onBackgroundPointerDown(event: PointerEvent) {
    if (event.button !== 0) return;
    this.marqueeState = {
      startX: event.clientX,
      startY: event.clientY,
      add: event.shiftKey || event.metaKey || event.ctrlKey,
      rect: null,
    };
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  /** Client px to page pt. */
  private toPagePoint(clientX: number, clientY: number): { x: number; y: number } {
    const box = this.pageEl().nativeElement.getBoundingClientRect();
    const factor = PX_PER_PT * this.scale();
    return { x: (clientX - box.left) / factor, y: (clientY - box.top) / factor };
  }

  // ------------------------------------------------------------ pointer

  onPointerStart(start: PointerStart) {
    const element = this.elements().find((e) => e.id === start.id);
    if (!element) return;
    const entry = this.entryFor(element);
    const ids = this.selectedIds();
    const companions =
      start.mode === 'move' && ids.has(start.id)
        ? this.elements()
            .filter((e) => ids.has(e.id) && e.id !== start.id)
            .map((e) => ({ id: e.id, rect: { x: e.x, y: e.y, width: e.width, height: e.height } }))
        : [];
    this.interaction = {
      id: start.id,
      mode: start.mode,
      handle: start.handle,
      startX: start.event.clientX,
      startY: start.event.clientY,
      startRect: { x: element.x, y: element.y, width: element.width, height: element.height },
      lockAspect: entry?.aspect_ratio != null || start.event.shiftKey,
      entry,
      companions,
      moved: false,
      wasSelected: !!start.wasSelected,
      natural: start.natural,
      startFocus: { x: element.focus_x ?? 0.5, y: element.focus_y ?? 0.5 },
    };
    this.dragging.set(true);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  onKeyResize(resize: KeyResize) {
    const element = this.elements().find((e) => e.id === resize.id);
    if (!element) return;
    const entry = this.entryFor(element);
    const rect = resizeRect(
      element,
      resize.handle,
      resize.dx,
      resize.dy,
      entry?.aspect_ratio != null,
      minSizeFor(entry),
    );
    this.elementCommit.emit([
      { id: resize.id, rect: clampRect(rect, this.page(), minSizeFor(entry)) },
    ]);
  }

  /** The rects of the dragged element and its companions for this pointer position. */
  private rectsFor(event: PointerEvent): { id: string; rect: Rect }[] | null {
    const it = this.interaction;
    if (!it) return null;
    const factor = PX_PER_PT * this.scale();
    let dx = (event.clientX - it.startX) / factor;
    let dy = (event.clientY - it.startY) / factor;
    const min = minSizeFor(it.entry);
    const page = this.page();
    if (it.mode === 'resize') {
      const lockAspect = it.lockAspect || event.shiftKey;
      let rect = resizeRect(it.startRect, it.handle!, dx, dy, lockAspect, min);
      // smart guides for the moving edges; with a locked ratio the nearest edge wins
      if (event.altKey || event.shiftKey) {
        this.guides.set({ x: [], y: [] });
      } else {
        const others = this.elements().filter((e) => e.id !== it.id);
        const element = this.elements().find((e) => e.id === it.id);
        const snapped = snapResize(
          rect,
          it.handle!,
          others,
          page,
          this.snapPx / factor,
          lockAspect,
          !(element && this.isText(element)),
        );
        if (snapped.rect.width >= min.width && snapped.rect.height >= min.height) {
          rect = snapped.rect;
          this.guides.set(snapped.guides);
        } else {
          this.guides.set({ x: [], y: [] });
        }
      }
      return [{ id: it.id, rect: clampRect(rect, page, min) }];
    }
    // a move keeps the whole selection on the page: clamp the shift, not each rect
    const all = [it.startRect, ...it.companions.map((c) => c.rect)];
    const bounds = selectionBounds(all)!;
    dx = Math.min(Math.max(dx, -bounds.x), page.width - bounds.x - bounds.width);
    dy = Math.min(Math.max(dy, -bounds.y), page.height - bounds.y - bounds.height);
    // smart guides: snap to the other elements and the page (Alt disables)
    if (event.altKey || event.shiftKey) {
      this.guides.set({ x: [], y: [] });
    } else {
      const moving = new Set([it.id, ...it.companions.map((c) => c.id)]);
      const others = this.elements().filter((e) => !moving.has(e.id));
      // text flush with the paper edge is rarely wanted: text snaps to the page's
      // centre lines only, pictures and boxes to its edges too
      const pageEdges = !this.elements().some((e) => moving.has(e.id) && this.isText(e));
      const snap = snapMove(
        { ...bounds, x: bounds.x + dx, y: bounds.y + dy },
        others,
        page,
        this.snapPx / factor,
        pageEdges,
      );
      dx = Math.min(Math.max(dx + snap.dx, -bounds.x), page.width - bounds.x - bounds.width);
      dy = Math.min(Math.max(dy + snap.dy, -bounds.y), page.height - bounds.y - bounds.height);
      this.guides.set(snap.guides);
    }
    return [
      { id: it.id, rect: { ...it.startRect, x: it.startRect.x + dx, y: it.startRect.y + dy } },
      ...it.companions.map((c) => ({
        id: c.id,
        rect: { ...c.rect, x: c.rect.x + dx, y: c.rect.y + dy },
      })),
    ];
  }

  private pointerMove(event: PointerEvent) {
    if (this.marqueeState) {
      const m = this.marqueeState;
      if (!m.rect && Math.hypot(event.clientX - m.startX, event.clientY - m.startY) < 4) return;
      const a = this.toPagePoint(m.startX, m.startY);
      const b = this.toPagePoint(event.clientX, event.clientY);
      m.rect = {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        width: Math.abs(a.x - b.x),
        height: Math.abs(a.y - b.y),
      };
      this.marquee.set(m.rect);
      this.dragging.set(true);
      return;
    }
    if (this.interaction?.mode === 'pan') {
      this.interaction.moved = true;
      this.focusChange.emit({ ...this.panFocus(event), commit: false });
      return;
    }
    const rects = this.rectsFor(event);
    if (rects && this.interaction) {
      this.interaction.moved = true;
      this.elementChange.emit(rects);
    }
  }

  /**
   * The crop focus for the pointer's position during a pan: the picture is scaled
   * to cover the box, the overflow on each axis is what the focus moves across.
   */
  private panFocus(event: PointerEvent): { id: string; focus_x: number; focus_y: number } {
    const it = this.interaction!;
    const element = this.elements().find((e) => e.id === it.id)!;
    const start = it.startFocus ?? { x: 0.5, y: 0.5 };
    const natural = it.natural;
    if (!natural) return { id: it.id, focus_x: start.x, focus_y: start.y };
    const cover = Math.max(element.width / natural.width, element.height / natural.height);
    const overflowX = natural.width * cover - element.width;
    const overflowY = natural.height * cover - element.height;
    const factor = PX_PER_PT * this.scale();
    const dx = (event.clientX - it.startX) / factor;
    const dy = (event.clientY - it.startY) / factor;
    const clamp01 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000;
    return {
      id: it.id,
      focus_x: overflowX > 0.5 ? clamp01(start.x - dx / overflowX) : start.x,
      focus_y: overflowY > 0.5 ? clamp01(start.y - dy / overflowY) : start.y,
    };
  }

  private pointerUp(event: PointerEvent) {
    if (this.marqueeState) {
      const m = this.marqueeState;
      this.marqueeState = null;
      this.marquee.set(null);
      if (m.rect) {
        const ids = this.elements()
          .filter((e) => rectsIntersect(m.rect!, e))
          .map((e) => e.id);
        this.selectMany.emit({ ids, add: m.add });
        this.stopListening();
        setTimeout(() => this.dragging.set(false));
      } else {
        this.stopListening(); // a plain click: onBackgroundClick deselects
      }
      return;
    }
    // A click without movement selects only; nothing to commit, nothing for the history.
    if (this.interaction?.mode === 'pan') {
      if (this.interaction.moved) this.focusChange.emit({ ...this.panFocus(event), commit: true });
      this.stopListening();
      setTimeout(() => this.dragging.set(false));
      return;
    }
    const rects = this.interaction?.moved ? this.rectsFor(event) : null;
    if (rects) this.elementCommit.emit(rects);
    // a second click on a part of the selected group: into the group
    if (this.interaction && !this.interaction.moved && this.interaction.wasSelected)
      this.drillIn.emit(this.interaction.id);
    this.stopListening();
    // The click that ends a drag must not deselect.
    setTimeout(() => this.dragging.set(false));
  }

  private stopListening() {
    this.interaction = null;
    this.marqueeState = null;
    this.guides.set({ x: [], y: [] });
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }
}
